import { Component, OnInit, OnDestroy, HostListener, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { StaffApiService } from '../../../core/api/services';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { PatientBipInfoModel } from '../../../core/api/models';
import { AlertService } from '../../../shared/services/alert.service';
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';
import { tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';

@Component({
    selector: 'app-bip-emergency-procedures',
    standalone: true,
    imports: [CommonModule, FormsModule, SharedModule, SelectButtonModule, CKEditorModule],
    templateUrl: './bip-emergency-procedures.component.html',
    styleUrl: '../../supervisor-main.component.css'
})
export class BipEmergencyProceduresComponent implements OnInit, OnDestroy {
    patientID = input.required<string>();
    loading = signal<boolean>(false);
    currentModel: PatientBipInfoModel | null = null;
    draftModel: PatientBipInfoModel | null = null;
    emergencyProcedures: string | null = null; // draft content
    initialEmergencyProcedures: string = '';
    public Editor = Editor;
    public editorConfig = editorConfig;
    isCurrentView: boolean = true;
    useOfflineData = input<boolean>(false);
    showToggle = input<boolean>(true);
    private autoSaveHandle: any;
    canEditBip: boolean = false;

    constructor(private staffApi: StaffApiService, private alerts: AlertService, private offlineDataService: OfflineDataService, private authService: AuthService) { }

    // Normalize optional input to a boolean defaulting to true
    shouldShowToggle(): boolean {
        const value = this.showToggle();
        return (value ?? true) && this.canEditBip;
    }

    ngOnInit(): void {
        this.canEditBip = this.authService.canEditBip();
        if (!this.canEditBip) {
            this.isCurrentView = true;
        }
        const useOffline = this.useOfflineData() || this.offlineDataService.isInOfflineMode();
        if (useOffline) {
            this.loadDataOffline();
        } else {
            this.loadData();
        }

        // Auto-save every 30s when in Draft view and draft model exists
        if (this.canEditBip) {
            this.scheduleAutoSave();
        }
    }

    private scheduleAutoSave(): void {
        this.autoSaveHandle = setTimeout(() => {
            if (!this.isCurrentView && this.draftModel) {
                this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
            }
            this.scheduleAutoSave();
        }, 30000);
    }
    loadData() {
        this.loading.set(true);
        if (!this.canEditBip) {
            this.staffApi.GetCurrentPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
                next: (data) => {
                    this.currentModel = data ?? null;
                    this.draftModel = null;
                    this.loading.set(false);
                },
                error: () => {
                    this.currentModel = null;
                    this.draftModel = null;
                    this.loading.set(false);
                }
            });
            return;
        }
        // Load current and draft in parallel
        this.staffApi.GetCurrentPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
            next: (data) => {
                this.currentModel = data ?? null;
            },
            error: () => {
                this.currentModel = null;
            }
        });

        this.staffApi.GetNewPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
            next: (data) => {
                this.draftModel = data ?? null;
                if (data == null || !this.draftModel?.EmergencyProcedures) {
                    if (!this.draftModel) {
                        this.draftModel = {};
                    }
                    this.draftModel.EmergencyProcedures = "<figure class=\"table\"><table class=\"ck-table-resized\" style=\"border-style:none;\"><colgroup><col style=\"width:50%;\"><col style=\"width:50%;\"></colgroup><tbody><tr><td style=\"background-color:#cc4125;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"2\"><h2 style=\"text-align:center;\"><strong>Emergency Procedures and Criteria</strong></h2></td></tr><tr><td style=\"background-color:#e6b8af;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"2\"><h4 style=\"text-align:center;\"><strong>Physical Management Criteria</strong></h4></td></tr><tr><td style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"2\"><ol><li><strong>Imminent risk of serious harm,&nbsp;</strong></li><li><strong>No other way to prevent harm without physical management, and;&nbsp;</strong></li><li><strong>The risk of not intervening is greater than the risk of intervening.&nbsp;</strong></li></ol></td></tr><tr><td style=\"background-color:#e6b8af;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"2\"><h5 style=\"text-align:center;\"><strong>Client Specific Physical Management Guide</strong></h5></td></tr><tr><td style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"2\"><ul><li><strong>[Fill]</strong></li></ul></td></tr><tr><td style=\"background-color:#e6b8af;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"2\"><h5 style=\"text-align:center;\"><strong>Client Specific Medical Protocol:</strong></h5></td></tr><tr><td style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"2\"><ul><li><strong>[Fill]</strong></li></ul></td></tr><tr><td style=\"background-color:#e6b8af;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><h5 style=\"text-align:center;\"><strong>Contacting Authorities</strong></h5></td><td style=\"background-color:#e6b8af;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><h5 style=\"text-align:center;\"><strong>Relevant Information</strong></h5></td></tr><tr><td style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><ul><li><strong>[Fill]</strong></li></ul></td><td style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><ul><li><strong>[Fill]</strong></li></ul></td></tr></tbody></table></figure>";
                }
                this.emergencyProcedures = this.draftModel?.EmergencyProcedures ?? null;
                this.initialEmergencyProcedures = this.emergencyProcedures ?? '';
                this.loading.set(false);
            },
            error: () => {
                this.draftModel = null;
                this.initialEmergencyProcedures = '';
                this.loading.set(false);
            }
        });
    }

    loadDataOffline() {
        this.loading.set(true);
        const data = this.offlineDataService.getOfflinePatientData(Number(this.patientID()));
        this.currentModel = data?.patientBipInfo ?? null;
        this.draftModel = null;
        this.emergencyProcedures = this.currentModel?.EmergencyProcedures ?? null;
        this.initialEmergencyProcedures = this.emergencyProcedures ?? '';
        this.loading.set(false);
    }

    getSaveObservable(reloadAfter: boolean = true) {
        if (!this.draftModel) return;
        this.draftModel.PatientID = Number(this.patientID());
        this.draftModel.EmergencyProcedures = this.emergencyProcedures ?? null;
        return this.staffApi.SavePatientBIPInfo({ body: this.draftModel }).pipe(
            tap((saved) => {
                this.draftModel = saved;
                if (reloadAfter) {
                    this.initialEmergencyProcedures = this.draftModel?.EmergencyProcedures ?? '';
                }
            })
        );
    }

    save() {
        if (!this.canEditBip) return;
        if (!this.draftModel) {
            this.alerts.error('No draft BIP exists for this patient.');
            return;
        }
        this.getSaveObservable()?.subscribe({
            next: (saved) => {
                this.alerts.success('Saved');
            },
            error: () => this.alerts.error('Failed to save')
        });
    }

    onEmergencyProceduresChange(event: any): void {
        if (event.editor) {
            const data = event.editor.getData();
            this.emergencyProcedures = data;
        }
    }

    onIsCurrentViewChange(event: any): void {
        if (!this.canEditBip) {
            this.isCurrentView = true;
            return;
        }
        const movingToCurrent = event?.value === true && this.isCurrentView === false;
        if (movingToCurrent && this.draftModel) {
            this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
        }
        if (this.isCurrentView) {
            this.initialEmergencyProcedures = this.emergencyProcedures ?? '';
        }
        this.isCurrentView = event.value;
    }

    ngOnDestroy(): void {
        // Final silent save when leaving page if in Draft
        if (!this.isCurrentView && this.draftModel) {
            this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
        }
        if (this.autoSaveHandle) {
            clearTimeout(this.autoSaveHandle);
            this.autoSaveHandle = null;
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload(event: Event) {
        if (!this.canEditBip) return;
        if (!this.isCurrentView && this.draftModel) {
            this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
        }
    }
}


